/* ============================================================================
   Luxury Interactive Engine
   ----------------------------------------------------------------------------
   Advanced interactivity for the Horizon theme, built on GSAP 3.12 +
   ScrollTrigger (loaded async from CDN by snippets/luxury-interactive-engine.liquid).

   Modules
     A) Cursor   - reactive lerp cursor + expanding label (follows the pointer
                   only; it never grabs, pulls or tilts any page element)
     C) Reveals  - kinetic staggered scroll reveals (ScrollTrigger.batch)
                   DISABLED (see boot()) — a GSAP filter+will-change animation
                   combined with clearProps was leaving fully-"revealed"
                   elements (product cards, headings) permanently unpainted /
                   blank for real visitors, even though every computed style
                   read back as completely normal (opacity:1, no filter, no
                   transform). Confirmed reproducible with real mouse-wheel
                   scrolling, not just automated scrollIntoView. Kept in the
                   file (unused) in case a future fix removes the `filter`
                   step and re-enables it safely.

   Hard rules
     - Bails completely in Shopify Design Mode and for prefers-reduced-motion.
     - Cursor only runs on (hover: hover) and (pointer: fine).
     - Animates transform / opacity / filter only — no layout writes.
     - Reveals only ever arm elements that are BELOW the fold at init, plus a
       4s failsafe, plus a full no-op path if the CDN fails: content is never
       left hidden.
     - Touches nothing owned by cart / variant-picker / quick-add / forms.
   ========================================================================== */

(function () {
  'use strict';

  var win = window;
  var doc = document;
  var html = doc.documentElement;

  var DESIGN_MODE = !!(win.Shopify && win.Shopify.designMode);
  var REDUCED_MOTION = win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE_POINTER = win.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var COARSE_POINTER = win.matchMedia('(pointer: coarse)').matches;

  if (DESIGN_MODE || REDUCED_MOTION) return;

  // VN fix: on touch-only devices (no fine pointer) the Cursor never runs
  // below (it is gated behind FINE_POINTER in boot()), the
  // Reveals module is separately disabled entirely regardless of pointer
  // type (see boot()), and every CSS rule tied to this engine in
  // luxury-interactive-engine.css is itself scoped to
  // `(hover: hover) and (pointer: fine)` or is an inert failsafe class that
  // is never added without it. So on a phone/tablet this whole engine does
  // nothing a visitor can see - yet it still loads GSAP + ScrollTrigger from
  // a CDN, registers a MutationObserver watching all of #MainContent, and
  // adds resize/pageshow listeners: pure background overhead competing for
  // the main thread, worst during scroll. Bailing out completely here
  // removes that overhead on mobile with zero visual change.
  if (!FINE_POINTER) return;

  var CONFIG = Object.assign(
    {
      gsapUrls: [
        'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js',
        'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js'
      ],
      cursorLabels: { product: 'DISCOVER', collection: 'VIEW' }
    },
    win.LuxuryEngineConfig || {}
  );

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = doc.querySelector('script[data-lie-src="' + src + '"], script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.lieLoaded || win.gsap) return resolve();
        existing.addEventListener('load', function () { resolve(); }, { once: true });
        existing.addEventListener('error', function () { reject(new Error(src)); }, { once: true });
        return;
      }
      var s = doc.createElement('script');
      s.src = src;
      s.async = true;
      s.setAttribute('data-lie-src', src);
      s.addEventListener('load', function () { s.dataset.lieLoaded = '1'; resolve(); }, { once: true });
      s.addEventListener('error', function () { reject(new Error(src)); }, { once: true });
      doc.head.appendChild(s);
    });
  }

  function gsapReady() { return !!(win.gsap && win.ScrollTrigger); }

  function injectFallback() {
    return CONFIG.gsapUrls.reduce(function (chain, url) {
      return chain.then(function () { return loadScript(url); });
    }, Promise.resolve());
  }

  function waitForGsap() {
    return new Promise(function (resolve, reject) {
      if (gsapReady()) return resolve();

      var elapsed = 0;
      var step = 60;
      var injected = false;

      var poll = win.setInterval(function () {
        if (gsapReady()) {
          win.clearInterval(poll);
          return resolve();
        }
        elapsed += step;
        if (elapsed >= 3000 && !injected) {
          injected = true;
          injectFallback().catch(function () {});
        }
        if (elapsed >= 8000) {
          win.clearInterval(poll);
          reject(new Error('gsap unavailable'));
        }
      }, step);
    });
  }

  waitForGsap().then(boot).catch(function () {
    releaseFailsafe();
  });

  function releaseFailsafe() {
    doc.querySelectorAll('.lie-reveal').forEach(function (el) {
      el.classList.add('lie-reveal--failsafe');
    });
  }

  function boot() {
    var gsap = win.gsap;
    var ScrollTrigger = win.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return releaseFailsafe();

    gsap.registerPlugin(ScrollTrigger);

    var pageWrapper = doc.querySelector('.page-wrapper');
    if (win.matchMedia('(min-width: 990px)').matches && pageWrapper) {
      ScrollTrigger.defaults({ scroller: pageWrapper });
    }

    html.classList.add('lie-ready');

    /* Reveals module intentionally NOT instantiated — see file header note.
       It was the cause of a confirmed bug where product cards and headings
       went permanently blank after their reveal animation "completed".
       Cursor never touches `filter`, so it is unaffected and stays enabled.
       (The Tilt module was removed on purpose: the cursor must not grab,
       tilt or displace any element of the page.) */
    var modules = [];
    if (FINE_POINTER) {
      modules.push(new Cursor(gsap));
    }

    modules.forEach(function (m) {
      try { m.init(); } catch (e) { /* isolate module failures */ }
    });

    var mainEl = doc.getElementById('MainContent');
    if (mainEl) {
      var rescanTimer = null;
      var mo = new MutationObserver(function (mutations) {
        var added = false;
        for (var i = 0; i < mutations.length && !added; i++) {
          var nodes = mutations[i].addedNodes || [];
          for (var j = 0; j < nodes.length; j++) {
            var n = nodes[j];
            if (n.nodeType !== 1) continue;
            added = true;
            break;
          }
        }
        if (!added) return;
        win.clearTimeout(rescanTimer);
        rescanTimer = win.setTimeout(function () {
          modules.forEach(function (m) {
            if (typeof m.rescan === 'function') { try { m.rescan(); } catch (e) {} }
          });
          ScrollTrigger.refresh();
        }, 150);
      });
      mo.observe(mainEl, { childList: true, subtree: true });
    }

    var refreshTimer = null;
    win.addEventListener('resize', function () {
      win.clearTimeout(refreshTimer);
      refreshTimer = win.setTimeout(function () { ScrollTrigger.refresh(); }, 200);
    });
    win.addEventListener('pageshow', function (e) { if (e.persisted) ScrollTrigger.refresh(); });
  }

  function belowFold(el) {
    var r = el.getBoundingClientRect();
    var vh = win.innerHeight || html.clientHeight;
    return r.top >= vh * 0.9;
  }

  function Cursor(gsap) { this.gsap = gsap; }

  Cursor.prototype.init = function () {
    var gsap = this.gsap;

    var ring = doc.createElement('div');
    ring.className = 'lie-cursor__ring';
    ring.setAttribute('aria-hidden', 'true');
    var label = doc.createElement('span');
    label.className = 'lie-cursor__label';
    ring.appendChild(label);

    var dot = doc.createElement('div');
    dot.className = 'lie-cursor';
    dot.setAttribute('aria-hidden', 'true');

    [ring, dot].forEach(function (el) {
      el.style.setProperty('position', 'fixed', 'important');
      // Absolute top of the stacking order — must clear third-party app
      // modals (e.g. size-chart apps) and the header's own drawers/search.
      el.style.setProperty('z-index', '2147483647', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('will-change', 'transform', 'important');
      el.style.setProperty('top', '0', 'important');
      el.style.setProperty('left', '0', 'important');
    });

    doc.body.appendChild(ring);
    doc.body.appendChild(dot);

    var pointer = { x: win.innerWidth / 2, y: win.innerHeight / 2 };
    var ringPos = { x: pointer.x, y: pointer.y };

    var setDot = gsap.quickSetter(dot, 'css');
    var setRing = gsap.quickSetter(ring, 'css');
    function dotTo(x, y) { setDot({ x: x, y: y }); }
    function ringTo(x, y) { setRing({ x: x, y: y }); }

    var MEDIA_SEL = '.product-card, .collection-card';
    // Size Charts - Jotly (and similarly-injected size-guide widgets) folded
    // into the same "interactive" hover state as native buttons/links, so
    // the ring-grow reacts correctly over them instead of falling back to
    // the plain dot — see the matching cursor:none reinforcement in the CSS.
    var JOTLY_SEL = '[class*="jotly"], [id*="jotly"], .size-chart-link, .size-chart-modal, [data-jotly-modal], [data-target*="size"], [aria-controls*="size"]';
    var INTERACTIVE_SEL = 'a, button, .button, [role="button"], summary, label, ' + JOTLY_SEL;
    var FOOTER_SEL = 'footer';
    var FOOTER_LIGHT_SEL = '.email-signup__input-group, input[type="email"]';
    /* Shadow-DOM header controls (Shopify's <shopify-account> lives inside
       `.account-button`) render their clickable button INSIDE a closed
       rendering tree that page CSS cannot reach, so `cursor: none` on the
       host never applies to it. A transparent guard on the outer wrapper
       keeps the pointer on OUR element instead. Only the wrapper is targeted
       (not `shopify-account` itself) so a single guard covers it — no nested
       duplicate. */
    var SHADOW_ICON_SEL = '.account-button';

    /* The cursor never grabs, pulls or displaces any element of the page: it
       only follows the pointer. (The magnetic pull that used to live here was
       removed on purpose.) */
    function setPointer(e) {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      if (!html.classList.contains('lie-cursor-active')) html.classList.add('lie-cursor-active');
      dotTo(pointer.x, pointer.y);
    }

    function tick() {
      dotTo(pointer.x, pointer.y);

      ringPos.x += (pointer.x - ringPos.x) * 0.18;
      ringPos.y += (pointer.y - ringPos.y) * 0.18;
      ringTo(ringPos.x, ringPos.y);
    }
    gsap.ticker.add(tick, false, true);

    win.addEventListener('pointermove', setPointer, { passive: true });
    if ('onpointerrawupdate' in win) {
      win.addEventListener('pointerrawupdate', setPointer, { passive: true });
    }
    doc.addEventListener('pointerdown', function () { ring.classList.add('is-down'); }, { passive: true });
    doc.addEventListener('pointerup', function () { ring.classList.remove('is-down'); }, { passive: true });
    html.addEventListener('mouseleave', function () {
      html.classList.remove('lie-cursor-active', 'lie-in-footer', 'lie-on-light');
    });
    html.addEventListener('mouseenter', function () { html.classList.add('lie-cursor-active'); });
    win.addEventListener('blur', function () {
      html.classList.remove('lie-cursor-active', 'lie-in-footer', 'lie-on-light');
    });

    doc.addEventListener('pointerover', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var media = t.closest(MEDIA_SEL);
      if (media) {
        label.textContent = media.classList.contains('collection-card')
          ? CONFIG.cursorLabels.collection
          : CONFIG.cursorLabels.product;
        ring.classList.add('is-media');
        dot.classList.add('is-hidden');
        return;
      }
      if (t.closest(INTERACTIVE_SEL)) ring.classList.add('is-interactive');

      // Footer colour inversion: white cursor only inside <footer>, and back
      // to solid black over the newsletter field's white patch — never
      // anywhere else in the store.
      if (t.closest(FOOTER_SEL)) html.classList.add('lie-in-footer');
      if (t.closest(FOOTER_LIGHT_SEL)) html.classList.add('lie-on-light');
    });

    doc.addEventListener('pointerout', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var to = e.relatedTarget instanceof Element ? e.relatedTarget : null;
      if (t.closest(MEDIA_SEL) && !(to && to.closest(MEDIA_SEL))) {
        ring.classList.remove('is-media');
        dot.classList.remove('is-hidden');
      }
      if (t.closest(INTERACTIVE_SEL) && !(to && to.closest(INTERACTIVE_SEL))) {
        ring.classList.remove('is-interactive');
      }
      if (t.closest(FOOTER_SEL) && !(to && to.closest(FOOTER_SEL))) {
        html.classList.remove('lie-in-footer');
      }
      if (t.closest(FOOTER_LIGHT_SEL) && !(to && to.closest(FOOTER_LIGHT_SEL))) {
        html.classList.remove('lie-on-light');
      }
    });

    /* ---- shadow-DOM header icon guard (see SHADOW_ICON_SEL above) ---- */
    this._guardShadowIcons = function (root) {
      (root || doc).querySelectorAll(SHADOW_ICON_SEL).forEach(function (host) {
        if (host.dataset.lieGuarded) return;
        host.dataset.lieGuarded = '1';

        var pos = win.getComputedStyle(host).position;
        if (pos === 'static') host.style.position = 'relative';

        var guard = doc.createElement('span');
        guard.className = 'lie-cursor-guard';
        guard.setAttribute('aria-hidden', 'true');
        host.appendChild(guard);

        // First pointerdown "releases" the guard so the click reaches the
        // real (shadow-DOM) control underneath and opens the account panel;
        // leaving the host re-arms it for the next hover.
        guard.addEventListener('pointerdown', function () {
          guard.classList.add('is-released');
        });
        host.addEventListener('pointerleave', function () {
          guard.classList.remove('is-released');
        });
      });
    };
    this._guardShadowIcons(doc);

    /* Note on native <dialog> top layer: the cursor's two elements stay
       permanently parented to <body> (appended once, above) with
       `position: fixed` — never moved. A previous version of this file
       temporarily re-parented them into an open <dialog> to get above
       showModal()'s top layer; that broke the cursor's render loop /
       coordinates and was reverted on purpose. The search modal itself no
       longer uses showModal() (see snippets/search-modal.liquid +
       assets/dialog.js), so the max z-index below is enough on its own. */
  };

  Cursor.prototype.rescan = function () {
    if (this._guardShadowIcons) this._guardShadowIcons(doc);
  };

  function Reveals(gsap, ScrollTrigger) {
    this.gsap = gsap;
    this.ScrollTrigger = ScrollTrigger;
    this.armed = [];
  }

  Reveals.SELECTOR = ['h1', 'h2', '.rte', '.prose', 'blockquote', '.product-card', '.collection-card', '[data-lie-reveal]'].join(',');

  Reveals.prototype.init = function () {
    this.arm(doc.getElementById('MainContent'));

    var self = this;
    win.setTimeout(function () {
      self.armed.forEach(function (el) {
        if (parseFloat(win.getComputedStyle(el).opacity) < 0.99) {
          el.classList.add('lie-reveal--failsafe');
        }
      });
    }, 4000);
  };

  Reveals.prototype.rescan = function () {
    var gsap = this.gsap;
    var fresh = [];
    doc.querySelectorAll('#MainContent .product-card, #MainContent .collection-card').forEach(function (el) {
      if (el.dataset.lieSeen) return;
      el.dataset.lieSeen = '1';
      fresh.push(el);
    });
    if (!fresh.length) return;
    gsap.fromTo(
      fresh,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: 0.6, stagger: 0.04, ease: 'power2.out', clearProps: 'transform,opacity' }
    );
  };

  Reveals.prototype.arm = function (scopeEl) {
    if (!scopeEl) return;
    var gsap = this.gsap;
    var ScrollTrigger = this.ScrollTrigger;

    var sections = Array.prototype.slice.call(scopeEl.querySelectorAll('.shopify-section')).filter(function (s) {
      return !s.parentElement || !s.parentElement.closest('.shopify-section');
    });
    if (!sections.length) sections = [scopeEl];

    var targets = [];
    var seen = new Set();

    sections.forEach(function (section) {
      Array.prototype.forEach.call(section.querySelectorAll(Reveals.SELECTOR), function (el) {
        if (seen.has(el) || el.dataset.lieSeen) return;
        if (el.parentElement && el.parentElement.closest('[data-lie-seen]')) return;
        seen.add(el);
        el.dataset.lieSeen = '1';
        if (!belowFold(el)) return;
        el.classList.add('lie-reveal');
        targets.push(el);
      });
    });

    if (!targets.length) return;
    this.armed = this.armed.concat(targets);

    var fromVars = { opacity: 0, y: 30, duration: 0.9, ease: 'power3.out', overwrite: true };
    if (!COARSE_POINTER) fromVars.filter = 'blur(4px)';

    ScrollTrigger.batch(targets, {
      start: 'top 88%',
      once: true,
      batchMax: 10,
      onEnter: function (batch) {
        var run = batch.filter(function (el) {
          if (el.dataset.lieRevealed) return false;
          el.dataset.lieRevealed = '1';
          return true;
        });
        if (!run.length) return;
        gsap.from(run, Object.assign({}, fromVars, {
          stagger: 0.09,
          willChange: 'transform, filter, opacity',
          clearProps: 'filter,transform,willChange,opacity'
        }));
      }
    });

    ScrollTrigger.refresh();
  };

  win.setTimeout(function () { if (!win.gsap) releaseFailsafe(); }, 9000);
})();
